<template>
    <div>
        <template v-if="!showTwoFactorValidation">
            <h2 class="mt-6 text-3xl leading-9 font-extrabold text-gray-900">
                {{ $t('auth.login.connectToAccount') }}
            </h2>

            <div class="mt-8">
                <form class="mt-6" @submit="logIn">
                    <apexo-input-group :label="$t('auth.login.email')">
                        <apexo-input
                            classes="w-full"
                            placeholder="example@apexo.ca"
                            @input="error = ''"
                            v-model="email"
                        />
                    </apexo-input-group>

                    <apexo-input-group class="mt-4" :label="$t('auth.login.password')">
                        <apexo-input
                            classes="w-full"
                            type="password"
                            @input="error = ''"
                            v-model="password"
                        />
                    </apexo-input-group>

                    <div class="flex rounded border border-red-200 mt-4 p-4 bg-red-100 text-red-600 text-sm" v-if="error">
                        {{ $t(error) }}
                    </div>

                    <apexo-button
                        class="mt-4"
                        :disabled="!isLoginEnabled"
                        @click="logIn"
                    >
                        {{ $t('auth.login.connect') }}
                    </apexo-button>

                    <div class="mt-6 flex items-center justify-between font-medium text-sm">
                        <router-link class="text-blue-500 hover:text-blue-300 focus:outline-none focus:underline transition ease-in-out duration-150" :to="{ name: 'auth.reset-password' }">
                            {{ $t('auth.login.forgotPassword') }}
                        </router-link>

                        <div class="cursor-pointer text-blue-500 hover:text-blue-300" @click="toggleLocale">
                            {{ localeString }}
                        </div>
                    </div>
                </form>
            </div>
        </template>

        <template v-else>
            <h2 class="mt-6 text-3xl leading-9 font-extrabold text-gray-900">
                {{ $t('auth.twoFactorChallenge.pageTitle') }}
            </h2>

            <p class="mt-6 flex flex-col items-center justify-between text-sm leading-5">
                {{ $t('auth.twoFactorChallenge.instructions') }}
            </p>

            <div class="mt-8">
                <form class="mt-6" @submit="verifyCode">
                    <apexo-input
                        classes="w-full"
                        name="twoFactorCode"
                        placeholder="999 999"
                        ref="twoFactorCode"
                        v-model="twoFactorCode"
                    />

                    <div class="flex rounded border border-red-200 mt-4 p-4 bg-red-100 text-red-600 text-sm" v-if="error">
                        {{ $t(error) }}
                    </div>

                    <apexo-button
                        class="mt-6"
                        :disabled="!isVerifyEnabled"
                        @click="verifyCode"
                    >
                        {{ $t('auth.twoFactorChallenge.validate') }}
                    </apexo-button>
                </form>
            </div>
        </template>
    </div>
</template>

<script>
    import { mapActions, mapState } from 'vuex';
    import ApexoInput from '../../components/Elements/ApexoInput.vue';
    import ApexoInputGroup from '../../components/Elements/ApexoInputGroup.vue';
    import ApexoButton from '../../components/Elements/ApexoButton.vue';

    export default {
        components: {
            ApexoButton,
            ApexoInput,
            ApexoInputGroup,
        },

        data() {
            return {
                email: '',
                password: '',
                error: '',
                showTwoFactorValidation: false,
                twoFactorCode: '',
            };
        },

        computed: {
            ...mapState([
                'authUser',
            ]),

            isEn() {
                return this.$i18n.locale === 'en';
            },

            localeString() {
                return this.isEn ? 'Français' : 'English';
            },

            isLoginEnabled() {
                return this.email && this.password;
            },

            isVerifyEnabled() {
                return !!this.twoFactorCode;
            },
        },

        methods: {
            ...mapActions([
                'fetchAuthUser',
            ]),

            async logIn() {
                if (!this.isLoginEnabled || this.$wait.is('authenticating')) {
                    return;
                }

                this.$wait.start('authenticating');

                try {
                    await this.$axios.get('sanctum/csrf-cookie');

                    const response = await this.$axios.post('/login', {
                        email: this.email,
                        password: this.password,
                    });

                    this.showTwoFactorValidation = response.data.two_factor;

                    if (!this.showTwoFactorValidation) {
                        await this.fetchAuthUser(this.email);

                        this.$router.push({ name: 'auth.twoFactorSetup' });
                    }
                } catch (error) {
                    if (error.response?.status === 422) {
                        this.error = 'auth.login.failed';
                    } else {
                        this.error = 'auth.login.error';
                    }

                    this.password = '';

                    this.$axios.post('/logout');
                } finally {
                    this.$wait.end('authenticating');
                }
            },

            async verifyCode() {
                if (!this.isVerifyEnabled) {
                    return;
                }

                try {
                    await this.$axios.post('/two-factor-challenge', {
                        code: this.twoFactorCode,
                    });
                } catch (error) {
                    this.error = 'auth.twoFactorChallenge.invalidCode';

                    this.reset();

                    return;
                }

                try {
                    await this.fetchAuthUser(this.email);

                    this.$router.push({ name: 'orders' });
                } catch (error) {
                    this.error = 'auth.login.error';

                    this.reset();

                    this.$axios.post('/logout');
                }
            },

            reset() {
                this.password = '';
                this.twoFactorCode = '';
                this.showTwoFactorValidation = false;
            },

            toggleLocale() {
                this.$i18n.locale = this.isEn ? 'fr' : 'en';
            },
        },

        beforeRouteEnter(to, from, next) {
            next(vm => {
                if (vm.authUser && vm.authUser.email) {
                    vm.$router.replace({ name: 'orders' });
                    return;
                }

                next();
            });
        },
    };
</script>
